import { useState } from 'react';
import { Select, Typography } from 'antd';
import type {
  ControlVerdict,
  EvaluationScenario,
  ScenarioCheck,
  ScenarioDecisionRef,
  ScenarioVersionChange,
  ScenarioVersionRef,
} from '@/api/scenarioVersions';
import { StatusPill } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import {
  CONTROL_WORDS,
  DECISION_WORDS,
  DOUBLE_OUTCOME_WORDS,
  ORIGIN_WORDS,
  REVIEWER_ROLES,
  checkName,
  checkSentence,
  shortHash,
  strengthWords,
  unchangedParts,
} from '../scenarioWords';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Pieces of the Quick review (screen 05) that read a version: what changed, the checks as rules, the source, and who
// wrote and approved each version. Shared by the review drawer and the advanced editor's Provenance section.
// ─────────────────────────────────────────────────────────────────────────────

export const surface = (isDark: boolean) => ({
  card: isDark ? colors.slate[800] : '#FFFFFF',
  page: isDark ? colors.slate[900] : colors.slate[50],
  line: isDark ? colors.slate[700] : colors.slate[200],
  muted: isDark ? colors.slate[400] : colors.slate[500],
  text: isDark ? colors.slate[100] : colors.slate[900],
});

export const cardStyle = (isDark: boolean) => {
  const s = surface(isDark);
  return { background: s.card, border: `1px solid ${s.line}`, borderRadius: 10, padding: 14 } as const;
};

const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

export function when(utc: string | null | undefined): string {
  if (!utc) return '';
  const d = new Date(utc);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The declared role: picked from the usual ones, remembered in this browser only (a convenience, never a record). */
const ROLE_KEY = 'agent-studio.scenario-review.role';

export function useDeclaredRole(): [string, (r: string) => void] {
  const [role, setRole] = useState<string>(() => {
    try {
      return window.localStorage.getItem(ROLE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const set = (r: string) => {
    setRole(r);
    try {
      window.localStorage.setItem(ROLE_KEY, r);
    } catch {
      /* private mode: the role is simply not remembered */
    }
  };
  return [role, set];
}

export function RolePicker({ value, onChange }: { value: string; onChange: (r: string) => void }) {
  return (
    <Select
      size="small"
      aria-label="Declared role"
      placeholder="Declare your role"
      value={value || undefined}
      onChange={onChange}
      style={{ minWidth: 190 }}
      options={REVIEWER_ROLES.map((r) => ({ value: r, label: r }))}
    />
  );
}

/** One check, as a sentence with its place and strength. */
export function CheckLine({ s, c, isDark, strike, sign, color }: {
  s: EvaluationScenario; c: ScenarioCheck; isDark: boolean; strike?: boolean; sign?: string; color?: string;
}) {
  const strength = strengthWords(c);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
      {sign && <span style={{ color, fontWeight: 700, width: 10 }} aria-hidden>{sign}</span>}
      <Text type="secondary" style={{ fontSize: 11.5, minWidth: 120 }}>{checkName(s, c)}</Text>
      <span style={{ flex: 1, minWidth: 200, textDecoration: strike ? 'line-through' : undefined, color: color ?? surface(isDark).text, fontSize: 13 }}>
        {checkSentence(c)}
      </span>
      <StatusPill tone={strength.tone}>{strength.label}</StatusPill>
    </div>
  );
}

/** "What changed?" between the compared version and the current one, in words. */
export function ChangeList({
  change,
  current,
  compared,
  isDark,
}: {
  change: ScenarioVersionChange;
  current: EvaluationScenario;
  compared: EvaluationScenario | null;
  isDark: boolean;
}) {
  const added = { color: isDark ? '#6EE7B7' : colors.green[700] };
  const removed = { color: isDark ? '#FCA5A5' : colors.red[700] };
  const row = { padding: '6px 0', borderTop: `1px dashed ${surface(isDark).line}` } as const;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {change.checks.length > 0 && <Text strong style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase' }}>Checks</Text>}
      {change.checks.map((c, i) => (
        <div key={i} style={row}>
          {c.change === 'Removed' && c.before && (
            <CheckLine s={compared ?? current} c={c.before} isDark={isDark} strike sign="−" color={removed.color} />
          )}
          {c.change === 'Added' && c.after && <CheckLine s={current} c={c.after} isDark={isDark} sign="+" color={added.color} />}
          {c.change === 'Changed' && c.after && c.before && (
            <div>
              <CheckLine s={current} c={c.after} isDark={isDark} />
              <Text type="secondary" style={{ fontSize: 12 }}>was: {checkSentence(c.before)}</Text>
            </div>
          )}
        </div>
      ))}
      {change.events.length > 0 && <Text strong style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 8 }}>What the user says</Text>}
      {change.events.map((e) => (
        <div key={e.turnIndex} style={row}>
          <Text type="secondary" style={{ fontSize: 11.5 }}>Turn {e.turnIndex + 1}: </Text>
          {e.before != null && <span style={{ ...removed, textDecoration: 'line-through' }}>“{e.before}”</span>}
          {e.before != null && e.after != null && ' → '}
          {e.after != null && <span style={added}>“{e.after}”</span>}
          {e.after == null && <Text type="secondary"> (turn removed)</Text>}
        </div>
      ))}
      {change.context.length > 0 && <Text strong style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 8 }}>Context</Text>}
      {change.context.map((c) => (
        <div key={c.part} style={row}>
          {contextLabel(c.part)}: {contextValue(c.part, c.before)} → {contextValue(c.part, c.after)}
        </div>
      ))}
      {(change.basisChanged || change.toolDoublesChanged || change.controlsChanged) && (
        <Text strong style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 8 }}>What it rests on and how it is proven</Text>
      )}
      {change.basisChanged && (
        <div style={row}>
          The hazard or the sources changed. <VersionPart label="Now" s={current} part="basis" />
        </div>
      )}
      {change.toolDoublesChanged && (
        <div style={row}>
          What a tool answers in this case changed. <VersionPart label="Now" s={current} part="doubles" />
        </div>
      )}
      {change.controlsChanged && (
        <div style={row}>
          The replies that prove the checks changed. <VersionPart label="Now" s={current} part="controls" />
        </div>
      )}
      {change.relationsChanged && <div style={row}>The sibling links changed (links only: nothing a run executes).</div>}
      {change.purposeChanged && (
        <div style={row}>
          <Text type="secondary" style={{ fontSize: 11.5 }}>Purpose: </Text>
          <span style={{ ...removed, textDecoration: 'line-through' }}>{change.purposeBefore || '(none)'}</span> → <span style={added}>{change.purposeAfter || '(none)'}</span>
        </div>
      )}
      {change.notesChanged && <div style={row}>The author’s notes changed (they are never executed).</div>}
      <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>{unchangedParts(change.parts)}</Text>
    </div>
  );
}

function contextLabel(part: string): string {
  return { Agent: 'Agent', Practice: 'Practice', Patient: 'Patient', Speaker: 'Who speaks', Protocol: 'Protocol', WorldRole: 'Named world', Scope: 'Evaluation scope' }[part] ?? part;
}

/** An id reads as #12; a named world and a scope read as their words. */
function contextValue(part: string, v: string | undefined): string {
  if (!v) return 'none';
  return part === 'WorldRole' || part === 'Scope' ? `“${v}”` : `#${v}`;
}

/** A version-3 part of the current version, in one line. */
function VersionPart({ label, s, part }: { label: string; s: EvaluationScenario; part: 'basis' | 'doubles' | 'controls' }) {
  const said =
    part === 'basis'
      ? [s.hazard ? `hazard “${s.hazard}”` : 'no hazard', `${(s.sources ?? []).length} source(s)`].join(', ')
      : part === 'doubles'
        ? (s.toolDoubles ?? []).map((d) => `${d.tool}: ${(DOUBLE_OUTCOME_WORDS[d.outcome] ?? d.outcome).toLowerCase()}${d.turnIndex == null ? '' : ` on turn ${d.turnIndex + 1}`}`).join('; ') || 'no tool is answered for'
        : (s.controls ?? []).map((c) => `${CONTROL_WORDS[c.kind]?.label ?? c.kind} on turn ${c.turnIndex + 1}`).join('; ') || 'no control';
  return <Text type="secondary" style={{ fontSize: 12 }}>{label}: {said}.</Text>;
}

/** How each control of a version behaves on its checks: a warning when one does not, never a block. */
export function ControlVerdictList({ verdicts, isDark }: { verdicts: ControlVerdict[]; isDark: boolean }) {
  if (verdicts.length === 0) return null;
  const bad = verdicts.filter((v) => !v.behaves);
  return (
    <div style={{ ...cardStyle(isDark), padding: 10, background: bad.length ? (isDark ? 'rgba(217,119,6,.12)' : colors.amber[100]) : undefined }} aria-label="Controls">
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        {bad.length === 0 ? `All ${verdicts.length} control(s) behave` : `${bad.length} of ${verdicts.length} control(s) do not behave`}
      </Text>
      {verdicts.map((v) => (
        <div key={v.controlId} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, marginBottom: 2 }}>
          <StatusPill tone={v.behaves ? 'green' : 'amber'}>{v.behaves ? 'Behaves' : 'Does not behave'}</StatusPill>
          <Text type="secondary">{CONTROL_WORDS[v.kind]?.label ?? v.kind} · turn {v.turnIndex + 1}</Text>
          <span>{v.observed}</span>
        </div>
      ))}
      {bad.length > 0 && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
          A control that does not behave shows a check too weak to trust. This is a warning: approving is still allowed.
        </Text>
      )}
    </div>
  );
}

/** Every check of a version, as rules. */
export function ChecksAsRules({ s, isDark }: { s: EvaluationScenario; isDark: boolean }) {
  if (s.checks.length === 0) return <Text type="secondary">This version has no checks, so a run assesses nothing.</Text>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {s.checks.map((c) => <CheckLine key={c.id} s={s} c={c} isDark={isDark} />)}
      <Text type="secondary" style={{ fontSize: 12 }}>Every check must hold (AND). Inside one check, “OR” marks alternatives where one is enough.</Text>
    </div>
  );
}

/**
 * The case beside what it came from: the hazard and the sources the version names (contract version 3), the origin, and
 * what the author wrote. A version that names no source says so: its clinical expectations stay pending SME review.
 */
export function SourcePanel({ s, isDark }: { s: EvaluationScenario; isDark: boolean }) {
  const refs = [...new Set(s.checks.map((c) => c.requirementRef).filter((r): r is string => !!r))];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
      <div style={cardStyle(isDark)} aria-label="The case">
        <Text strong style={{ display: 'block', marginBottom: 6 }}>The case</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>Purpose</Text>
        <div style={{ marginBottom: 6 }}>{s.purpose || '(none)'}</div>
        {s.events.map((e) => (
          <div key={e.id} style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Turn {e.turnIndex + 1}, the user says </Text>“{e.text}”
          </div>
        ))}
        <Text type="secondary" style={{ fontSize: 12 }}>Requirements</Text>
        <div>
          {refs.length === 0 ? 'None linked yet.' : refs.map((r) => <div key={r} style={mono}>{r}</div>)}
          <StatusPill tone="amber">Pending SME review</StatusPill>
        </div>
      </div>
      <div style={cardStyle(isDark)} aria-label="Source">
        <Text strong style={{ display: 'block', marginBottom: 6 }}>Source</Text>
        <div style={{ marginBottom: 6 }}>
          {ORIGIN_WORDS[s.provenance?.origin ?? ''] ?? 'Origin not recorded'}
          {s.provenance?.sourcePath && <div style={{ ...mono, fontSize: 11.5, wordBreak: 'break-all' }}>{s.provenance.sourcePath}</div>}
        </div>
        {s.hazard && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Hazard</Text>
            <div style={{ marginBottom: 6 }}>{s.hazard}</div>
          </>
        )}
        {(s.sources ?? []).length > 0 && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Sources the version names</Text>
            {s.sources!.map((src, i) => (
              <div key={i} style={{ marginBottom: 4, fontSize: 12.5 }}>
                <span style={{ ...mono, wordBreak: 'break-all' }}>{src.ref}</span>
                {[src.section, src.revision].filter(Boolean).length > 0 && <Text type="secondary"> · {[src.section, src.revision].filter(Boolean).join(' · ')}</Text>}
                {src.note && <div>{src.note}</div>}
              </div>
            ))}
          </>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>What the author wrote (never executed)</Text>
        {(s.notes ?? []).length === 0 ? (
          <div>No notes.</div>
        ) : (
          (s.notes ?? []).map((n, i) => <div key={i} style={{ fontSize: 12.5, marginBottom: 4 }}>{n.text}</div>)
        )}
        {(s.sources ?? []).length === 0 ? (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: isDark ? 'rgba(217,119,6,.12)' : colors.amber[100], fontSize: 12.5 }}>
            This version names no source (playbook, label, guideline), so every clinical expectation here stays pending SME review.
          </div>
        ) : (
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
            Studio records the sources the author named; it does not open or check them. Check the expectations against them.
          </Text>
        )}
      </div>
    </div>
  );
}

/** Who wrote each version and every decision, beside the version and hash it was bound to (frame C1). */
export function ReviewRecord({
  versions,
  decisions,
  currentNumber,
  isDark,
  unsaved,
}: {
  versions: ScenarioVersionRef[];
  decisions: ScenarioDecisionRef[];
  currentNumber?: number;
  isDark: boolean;
  /** The editor's unsaved changes, shown as the next version. */
  unsaved?: boolean;
}) {
  const s = surface(isDark);
  const newestFirst = [...versions].sort((a, b) => b.number - a.number);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} aria-label="Review record">
      {unsaved && (
        <div style={{ borderLeft: `3px solid ${colors.violet[500]}`, paddingLeft: 10 }}>
          <Text strong>Version {(versions.reduce((m, v) => Math.max(m, v.number), 0) || 0) + 1}</Text> <Text type="secondary">Your unsaved changes.</Text>
        </div>
      )}
      {newestFirst.map((v) => {
        const mine = decisions.filter((d) => d.snapshotId === v.snapshotId);
        return (
          <div key={v.snapshotId} style={{ borderLeft: `3px solid ${s.line}`, paddingLeft: 10 }}>
            <div>
              <Text strong>Version {v.number}</Text>{' '}
              <Text>by {v.authorName ?? (v.authorUserId ? `user #${v.authorUserId}` : 'an author not recorded')}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}> · {when(v.savedAtUtc)} · {ORIGIN_WORDS[v.origin ?? ''] ?? (v.schemaVersion >= 2 ? 'Database version' : 'File version')} · </Text>
              <span style={{ ...mono, fontSize: 11.5 }}>{shortHash(v.bodyHash)}</span>
              {v.number === currentNumber && <> <StatusPill tone="violet">Current</StatusPill></>}
            </div>
            {mine.map((d) => (
              <div key={d.id} style={{ marginTop: 4, fontSize: 12.5 }}>
                <StatusPill tone={DECISION_WORDS[d.decision].tone}>{DECISION_WORDS[d.decision].label}</StatusPill>{' '}
                by {d.byName ?? `user #${d.byUserId}`} · declared role: {d.role ?? 'not declared'} · {when(d.atUtc)} · bound to{' '}
                <span style={{ ...mono, fontSize: 11.5 }}>{shortHash(d.bodyHash)}</span>
                {d.isSelfApproval && <> <StatusPill tone="amber">Self-approval</StatusPill></>}
                {d.batchId && <Text type="secondary"> · approved in a batch</Text>}
                {d.decision === 'Approved' && currentNumber != null && v.number !== currentNumber && (
                  <Text type="secondary"> · not carried to version {currentNumber}</Text>
                )}
                {d.justification && <div style={{ color: s.muted }}>“{d.justification}”</div>}
              </div>
            ))}
          </div>
        );
      })}
      <Text type="secondary" style={{ fontSize: 12 }}>
        Clinical sign-off is not recorded in Studio: it is your practice’s convention (<span style={mono}>_SME_REVIEW.md</span>). A recorded
        approval is not clinical sign-off, and Studio does not read or check that file.
      </Text>
    </div>
  );
}
